import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Decorative } from "@faire/design-tokens";
import { ShowroomBadge } from "@faire/retailer-visitor-shared/components/Showroom/ShowroomBadge";
import { TrackedLinkContext } from "@faire/retailer-visitor-shared/components/TrackedLink/index";
import { useFaireDirectBrand } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { canAddToCart } from "@faire/retailer-visitor-shared/lib/canAddToCart";
import { ENABLE_CONSUMER_MODE } from "@faire/retailer-visitor-shared/lib/consts";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { popSignUp, SignUpElement, SignUpType } from "@faire/retailer-visitor-shared/lib/popSignUp";
import { getSettingEnableRetailerExclusivityAgreements } from "@faire/retailer-visitor-shared/settings/getSettingEnableRetailerExclusivityAgreement";
import { useSettingFaireDirectPDPSignup } from "@faire/retailer-visitor-shared/settings/useSettingFaireDirectPDPSignup";
import { useAssignSettingOwFImproveTermsTouchpointsPDPShipping, useSettingOwFImproveTermsTouchpointsPDPShipping } from "@faire/retailer-visitor-shared/settings/useSettingOwFImproveTermsTouchpointsPDPShipping";
import { AddToCartVariants } from "@faire/retailer-visitor-shared/stores/domain/CartStore/triggerAddToCartEvent";
import { useMarketStore } from "@faire/retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { getProductSubheaderName } from "@faire/retailer-visitor-shared/views/product/__internal__/SEOMetaText";
import { ProductAddToCart } from "@faire/retailer-visitor-shared/views/product/ProductDetails/AddToCart/ProductAddToCart";
import { ProductDetailsHeader } from "@faire/retailer-visitor-shared/views/product/ProductDetails/Header";
import { ShippingDetailsList } from "@faire/retailer-visitor-shared/views/product/ProductDetails/ShippingDetailsList";
import { getProductDetails, getProductOptions } from "@faire/retailer-visitor-shared/views/product/utils/productUtils";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { Storage } from "@faire/web--source/common/Storage";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackSignupModalOpenPdpMainCtaClick as trackSignupPdpMainCtaClick } from "@faire/web-api--source/events/signup/click/modalOpenPdpMainCta";
import { IBrandPromoCollection } from "@faire/web-api--source/indigofair/data/IBrandPromoCollection";
import { IClickInfo } from "@faire/web-api--source/indigofair/data/IClickInfo";
import { IDisclaimer } from "@faire/web-api--source/indigofair/data/IDisclaimer";
import { IInformationMessage } from "@faire/web-api--source/indigofair/data/IInformationMessage";
import { IProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { IInsiderModalRequest } from "@faire/web-api--source/indigofair/insider/IInsiderModalRequest";
import { observer } from "mobx-react";
import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { InsiderLabel } from "@faire/retailer/components/InsiderLabel/InsiderLabel";
import { useFaireDirectSignUp } from "@faire/retailer/hooks/FaireDirect/useFaireDirectSignUp/useFaireDirectSignUp";
import { useSettingEnablePdpAttributesEnrichment } from "@faire/retailer/settings/useSettingEnablePdpAttributesEnrichment";
import { TopRetailers } from "@faire/retailer/views/product/ProductDetails/__internal__/TopRetailers/TopRetailers";
import { ProductDetailsAccordion } from "../ProductDescriptionAccordions/ProductDetailsAccordion";
import { ProductPromoBanners } from "./__internal__/ProductPromoBanners/ProductPromoBanners";
import { Disclaimer } from "./AddToCart/Disclaimer";
import { ExclusivityLabel } from "./ExclusivityLabel";
import { LoggedInOnlyNote } from "./LoggedInOnlyNote";
var ProductDetailsBase = function(param) {
    var className = param.className, brand = param.brand, product = param.product, productAddToCartState = param.productAddToCartState, brandReviewSummary = param.brandReviewSummary, importShipmentInfo = param.importShipmentInfo, infoTexts = param.infoTexts, showElevatedMPPProductExperience = param.showElevatedMPPProductExperience, disclaimers = param.disclaimers, preselectedProductOptionToken = param.preselectedProductOptionToken, badges = param.badges, sampleDetails = param.sampleDetails, afterAddToCart = param.afterAddToCart, showSideCart = param.showSideCart;
    var _product_purchase_information_non_returnable_reason_data, _product_purchase_information, _productAddToCartState_productPromotionState_promotionLabel, _productAddToCartState_productPromotionState, _productAddToCartState_productPromotionState1;
    var viewport = useViewport();
    var settingOwFImproveTermsTouchpointsPDPShipping = useSettingOwFImproveTermsTouchpointsPDPShipping();
    var assignSettingOwFImproveTermsTouchpointsPDPShipping = useAssignSettingOwFImproveTermsTouchpointsPDPShipping();
    useEffect(function() {
        assignSettingOwFImproveTermsTouchpointsPDPShipping();
    }, [
        assignSettingOwFImproveTermsTouchpointsPDPShipping
    ]);
    var brandPromotion = useBrandPromotion(brand, product);
    var isConsumerMode = Storage.session.getItem(ENABLE_CONSUMER_MODE) === "true" && !isLoggedInRetailerOrBrandPreview();
    var showAddToCartCTA = product.state === IProduct.State.FOR_SALE || getProductOptions(product).length > 0;
    var nonReturnableReason = (_product_purchase_information = product.purchase_information) === null || _product_purchase_information === void 0 ? void 0 : (_product_purchase_information_non_returnable_reason_data = _product_purchase_information.non_returnable_reason_data) === null || _product_purchase_information_non_returnable_reason_data === void 0 ? void 0 : _product_purchase_information_non_returnable_reason_data.default_message;
    var promotionLabelText = (_productAddToCartState_productPromotionState = productAddToCartState.productPromotionState) === null || _productAddToCartState_productPromotionState === void 0 ? void 0 : (_productAddToCartState_productPromotionState_promotionLabel = _productAddToCartState_productPromotionState.promotionLabel) === null || _productAddToCartState_productPromotionState_promotionLabel === void 0 ? void 0 : _productAddToCartState_productPromotionState_promotionLabel.text;
    var insiderLabel = (_productAddToCartState_productPromotionState1 = productAddToCartState.productPromotionState) === null || _productAddToCartState_productPromotionState1 === void 0 ? void 0 : _productAddToCartState_productPromotionState1.insiderLabel;
    var showShowroomBadge = showElevatedMPPProductExperience && viewport.isTabletAndBelow;
    var safetyInformation = disclaimers === null || disclaimers === void 0 ? void 0 : disclaimers.filter(function(disclaimer) {
        return disclaimer.type === IDisclaimer.Type.FACE_MASK;
    });
    var otherDisclaimers = disclaimers === null || disclaimers === void 0 ? void 0 : disclaimers.filter(function(disclaimer) {
        return disclaimer.type !== IDisclaimer.Type.FACE_MASK;
    });
    return /*#__PURE__*/ _jsxs(Wrapper, {
        className: className,
        children: [
            showShowroomBadge ? /*#__PURE__*/ _jsx(StyledShowroomBadge, {}) : null,
            /*#__PURE__*/ _jsx(ProductDetailsHeader, {
                brand: brand,
                product: getProductDetails(product),
                selectedOption: productAddToCartState.selectedOption,
                brandReviewSummary: brandReviewSummary,
                productPromotionsState: productAddToCartState.productPromotionState,
                perStyleOptionVariations: productAddToCartState.perStyleOptionVariations,
                brandPromotion: brandPromotion,
                badges: badges,
                showOutOfStockUnderProductName: false,
                hideBrandDetails: viewport.isDesktopAndAbove && canAddToCart()
            }),
            isLoggedInRetailerOrBrandPreview() || isConsumerMode ? /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            }) : /*#__PURE__*/ _jsx(SignUpEntryPoint, {
                productToken: product.token,
                brandToken: brand.token
            }),
            isConsumerMode ? /*#__PURE__*/ _jsx(TopRetailers, {
                productToken: product.token
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    showAddToCartCTA ? /*#__PURE__*/ _jsx(TrackedLinkContext.Provider, {
                        value: {
                            type: IClickInfo.Type.PRODUCT,
                            containerName: "product_page"
                        },
                        children: /*#__PURE__*/ _jsx(ProductAddToCart, {
                            brand: brand,
                            product: product,
                            productAddToCartState: productAddToCartState,
                            preselectedProductOptionToken: preselectedProductOptionToken,
                            allowCustomOption: !viewport.isMobile,
                            sampleDetails: sampleDetails,
                            importShipmentInfo: importShipmentInfo,
                            infoTexts: infoTexts,
                            variant: AddToCartVariants.PDP_ADD_BTN,
                            afterAddToCart: afterAddToCart,
                            showSideCart: showSideCart
                        })
                    }) : null,
                    /*#__PURE__*/ _jsx(Subheader, {
                        brandCompanyName: brand.name
                    }),
                    settingOwFImproveTermsTouchpointsPDPShipping ? /*#__PURE__*/ _jsx(BelowAddToCartDetailsUpdate, {
                        nonReturnableReason: nonReturnableReason,
                        brand: brand,
                        importShipmentInfo: importShipmentInfo,
                        infoTexts: infoTexts,
                        safetyInformation: safetyInformation,
                        promotionLabelText: promotionLabelText,
                        productAddToCartState: productAddToCartState,
                        product: product
                    }) : /*#__PURE__*/ _jsx(BelowAddToCartDetails, {
                        nonReturnableReason: nonReturnableReason,
                        brand: brand,
                        importShipmentInfo: importShipmentInfo,
                        infoTexts: infoTexts,
                        safetyInformation: safetyInformation,
                        promotionLabelText: promotionLabelText,
                        insiderLabel: insiderLabel,
                        productAddToCartState: productAddToCartState,
                        product: product
                    }),
                    /*#__PURE__*/ _jsx(Disclaimers, {
                        disclaimers: otherDisclaimers
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: {
                            mobileAndAbove: 0,
                            tabletAndAbove: "2x"
                        }
                    })
                ]
            })
        ]
    });
};
var BelowAddToCartDetailsBase = function(param) {
    var nonReturnableReason = param.nonReturnableReason, brand = param.brand, importShipmentInfo = param.importShipmentInfo, infoTexts = param.infoTexts, safetyInformation = param.safetyInformation, promotionLabelText = param.promotionLabelText, insiderLabel = param.insiderLabel, productAddToCartState = param.productAddToCartState, product = param.product;
    var _product_details_product_information_section_groups, _product_details, _productAddToCartState_productPromotionState, _productAddToCartState_productPromotionState1, _productAddToCartState_productPromotionState2;
    var detailsInformationSection = (_product_details = product.details) === null || _product_details === void 0 ? void 0 : (_product_details_product_information_section_groups = _product_details.product_information_section_groups) === null || _product_details_product_information_section_groups === void 0 ? void 0 : _product_details_product_information_section_groups.find(function(group) {
        return group.type === "DETAILS_SECTION";
    });
    var pdpAttributesEnrichmentEnabled = useSettingEnablePdpAttributesEnrichment();
    var _brand_token;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isLoggedInRetailerOrBrandPreview() ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(LoggedInOnlyNote, {
                        nonReturnableReason: nonReturnableReason,
                        product: product,
                        selectedOption: productAddToCartState.selectedOption
                    }),
                    /*#__PURE__*/ _jsx(ShippingDetailsList, {
                        brand: brand,
                        importShipmentInfo: importShipmentInfo,
                        infoTexts: infoTexts,
                        shippingPromotion: (_productAddToCartState_productPromotionState = productAddToCartState.productPromotionState) === null || _productAddToCartState_productPromotionState === void 0 ? void 0 : _productAddToCartState_productPromotionState.guaranteedFreeShippingPromo,
                        isPreorder: productAddToCartState.showPreorderMessaging
                    })
                ]
            }) : null,
            (safetyInformation === null || safetyInformation === void 0 ? void 0 : safetyInformation.length) ? /*#__PURE__*/ _jsx(SafetyInformation, {
                disclaimers: safetyInformation
            }) : null,
            /*#__PURE__*/ _jsx(PromotionLabel, {
                text: promotionLabelText
            }),
            /*#__PURE__*/ _jsx(InsiderLabelRenderer, {
                insiderLabel: insiderLabel
            }),
            getSettingEnableRetailerExclusivityAgreements() ? /*#__PURE__*/ _jsx(ExclusivityLabel, {
                brandToken: (_brand_token = brand.token) !== null && _brand_token !== void 0 ? _brand_token : ""
            }) : null,
            /*#__PURE__*/ _jsx(ProductPromoBanners, {
                promotions: (_productAddToCartState_productPromotionState1 = productAddToCartState.productPromotionState) === null || _productAddToCartState_productPromotionState1 === void 0 ? void 0 : _productAddToCartState_productPromotionState1.nonPromotionStyleBanners,
                position: IInformationMessage.Position.DEFAULT
            }),
            /*#__PURE__*/ _jsx(ProductPromoBanners, {
                promotions: (_productAddToCartState_productPromotionState2 = productAddToCartState.productPromotionState) === null || _productAddToCartState_productPromotionState2 === void 0 ? void 0 : _productAddToCartState_productPromotionState2.nonFashionWeekPromos,
                position: IInformationMessage.Position.BOTTOM
            }),
            pdpAttributesEnrichmentEnabled ? /*#__PURE__*/ _jsx(ProductDetailsAccordion, {
                productOption: productAddToCartState.selectedOption,
                detailsInformationSection: detailsInformationSection === null || detailsInformationSection === void 0 ? void 0 : detailsInformationSection.sections.find(function(section) {
                    return section.type === "DETAILS";
                }),
                maxPreviewLines: detailsInformationSection === null || detailsInformationSection === void 0 ? void 0 : detailsInformationSection.max_preview_lines
            }) : null
        ]
    });
};
var BelowAddToCartDetails = observer(BelowAddToCartDetailsBase);
var BelowAddToCartDetailsUpdateBase = function(param) {
    var nonReturnableReason = param.nonReturnableReason, brand = param.brand, importShipmentInfo = param.importShipmentInfo, infoTexts = param.infoTexts, promotionLabelText = param.promotionLabelText, safetyInformation = param.safetyInformation, productAddToCartState = param.productAddToCartState, product = param.product;
    var _product_details_product_information_section_groups, _product_details, _productAddToCartState_productPromotionState, _productAddToCartState_productPromotionState1, _productAddToCartState_productPromotionState2;
    var detailsInformationSection = (_product_details = product.details) === null || _product_details === void 0 ? void 0 : (_product_details_product_information_section_groups = _product_details.product_information_section_groups) === null || _product_details_product_information_section_groups === void 0 ? void 0 : _product_details_product_information_section_groups.find(function(group) {
        return group.type === "DETAILS_SECTION";
    });
    var pdpAttributesEnrichmentEnabled = useSettingEnablePdpAttributesEnrichment();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            isLoggedInRetailerOrBrandPreview() ? /*#__PURE__*/ _jsx(LoggedInOnlyNote, {
                nonReturnableReason: nonReturnableReason,
                product: product,
                selectedOption: productAddToCartState.selectedOption
            }) : null,
            (safetyInformation === null || safetyInformation === void 0 ? void 0 : safetyInformation.length) ? /*#__PURE__*/ _jsx(SafetyInformation, {
                disclaimers: safetyInformation
            }) : null,
            /*#__PURE__*/ _jsx(PromotionLabel, {
                text: promotionLabelText
            }),
            /*#__PURE__*/ _jsx(ProductPromoBanners, {
                promotions: (_productAddToCartState_productPromotionState = productAddToCartState.productPromotionState) === null || _productAddToCartState_productPromotionState === void 0 ? void 0 : _productAddToCartState_productPromotionState.nonPromotionStyleBanners,
                position: IInformationMessage.Position.DEFAULT
            }),
            /*#__PURE__*/ _jsx(ProductPromoBanners, {
                promotions: (_productAddToCartState_productPromotionState1 = productAddToCartState.productPromotionState) === null || _productAddToCartState_productPromotionState1 === void 0 ? void 0 : _productAddToCartState_productPromotionState1.nonFashionWeekPromos,
                position: IInformationMessage.Position.BOTTOM
            }),
            isLoggedInRetailerOrBrandPreview() ? /*#__PURE__*/ _jsx(ShippingDetailsList, {
                brand: brand,
                importShipmentInfo: importShipmentInfo,
                infoTexts: infoTexts,
                shippingPromotion: (_productAddToCartState_productPromotionState2 = productAddToCartState.productPromotionState) === null || _productAddToCartState_productPromotionState2 === void 0 ? void 0 : _productAddToCartState_productPromotionState2.guaranteedFreeShippingPromo,
                isPreorder: productAddToCartState.showPreorderMessaging
            }) : null,
            pdpAttributesEnrichmentEnabled ? /*#__PURE__*/ _jsx(ProductDetailsAccordion, {
                productOption: productAddToCartState.selectedOption,
                detailsInformationSection: detailsInformationSection === null || detailsInformationSection === void 0 ? void 0 : detailsInformationSection.sections.find(function(section) {
                    return section.type === "DETAILS";
                }),
                maxPreviewLines: detailsInformationSection === null || detailsInformationSection === void 0 ? void 0 : detailsInformationSection.max_preview_lines
            }) : null
        ]
    });
};
var BelowAddToCartDetailsUpdate = observer(BelowAddToCartDetailsUpdateBase);
var SafetyInformation = function(param) {
    var disclaimers = param.disclaimers;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            disclaimers === null || disclaimers === void 0 ? void 0 : disclaimers.map(function(disclaimer) {
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "3x"
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraphSansMedium",
                            children: disclaimer.title
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "1x"
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: disclaimer.message
                        }, disclaimer.message)
                    ]
                });
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "1x"
            })
        ]
    });
};
var Disclaimers = function(param) {
    var disclaimers = param.disclaimers;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: (disclaimers === null || disclaimers === void 0 ? void 0 : disclaimers.length) ? disclaimers.map(function(disclaimer) {
            return /*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        size: "1x"
                    }),
                    /*#__PURE__*/ _jsx(Disclaimer, {
                        disclaimer: disclaimer
                    })
                ]
            }, disclaimer.message);
        }) : null
    });
};
var InsiderLabelRenderer = function(param) {
    var insiderLabel = param.insiderLabel;
    if (!(insiderLabel === null || insiderLabel === void 0 ? void 0 : insiderLabel.text)) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsx(InsiderLabel, {
                insiderLabel: insiderLabel,
                tooltipPosition: "left",
                context: IInsiderModalRequest.InsiderModalContext.PDP
            })
        ]
    });
};
var PromotionLabel = function(param) {
    var text = param.text;
    if (!text) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "paragraphSansMedium",
                color: Decorative.shade.orange,
                children: text
            })
        ]
    });
};
var Subheader = function(param) {
    var brandCompanyName = param.brandCompanyName;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var isFaireDirectActiveInLoggedOut = useFaireDirectBrand().isFaireDirectActiveInLoggedOut;
    var productSubheaderName = getProductSubheaderName({
        brandCompanyName: brandCompanyName,
        isFaireDirectActiveInLoggedOut: isFaireDirectActiveInLoggedOut
    }, strictLocalize);
    if (!productSubheaderName) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsx(Typography, {
                as: "h2",
                children: productSubheaderName
            })
        ]
    });
};
var SignUpFromModalCTA = function() {
    var isFaireDirectActiveInLoggedOut = useFaireDirectBrand().isFaireDirectActiveInLoggedOut;
    if (isFaireDirectActiveInLoggedOut) {
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "LQtL9s",
            defaultMessage: [
                {
                    type: 0,
                    value: "Create a retailer account"
                }
            ]
        });
    } else {
        return /*#__PURE__*/ _jsx(StrictLocalMsg, {
            id: "wYr4p9",
            defaultMessage: [
                {
                    type: 0,
                    value: "Unlock wholesale price"
                }
            ]
        });
    }
};
var SignUpEntryPoint = function(param) {
    var productToken = param.productToken, brandToken = param.brandToken;
    var history = useHistory();
    var settingFaireDirectPDPSignup = useSettingFaireDirectPDPSignup();
    var _useFaireDirectSignUp = useFaireDirectSignUp(), isFaireDirectSignUpEligible = _useFaireDirectSignUp.isFaireDirectSignUpEligible, faireDirectSignupRequiredEmailAddressSignUpRoute = _useFaireDirectSignUp.faireDirectSignupRequiredEmailAddressSignUpRoute;
    var openSignUpModal = function() {
        return popSignUp(history, SignUpType.PRODUCT, trackSignupPdpMainCtaClick, {
            element: SignUpElement.LIST,
            sourceId: "product_details_sign_up_entry_point"
        }, {
            product: productToken,
            brand: brandToken
        });
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            }),
            settingFaireDirectPDPSignup && isFaireDirectSignUpEligible ? /*#__PURE__*/ _jsx(Button, {
                linkTo: faireDirectSignupRequiredEmailAddressSignUpRoute,
                onClick: trackSignupPdpMainCtaClick,
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "TwVYbp",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Sign up for free"
                        }
                    ]
                })
            }) : /*#__PURE__*/ _jsx(Button, {
                "data-test-id": "product-sign-up-button",
                onClick: openSignUpModal,
                children: /*#__PURE__*/ _jsx(SignUpFromModalCTA, {})
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            }),
            /*#__PURE__*/ _jsx(Typography, {
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "GthZh5",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Already have a retailer account? "
                        },
                        {
                            type: 8,
                            value: "TextLinkFormatting",
                            children: [
                                {
                                    type: 0,
                                    value: "Sign in"
                                }
                            ]
                        }
                    ],
                    values: {
                        TextLinkFormatting: function(chunks) {
                            return renderTextLinkFormatting(chunks, productToken);
                        }
                    }
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            })
        ]
    });
};
var renderTextLinkFormatting = function(chunks, productToken) {
    return /*#__PURE__*/ _jsx(EmbeddedLink, {
        linkTo: getCurrentPath().append({
            signIn: "product",
            product: productToken
        }).toString(),
        children: chunks
    });
};
var useBrandPromotion = function(brand, product) {
    var isLive = useMarketStore([
        "isLive"
    ]).isLive;
    return useMemo(function() {
        var _brand_active_promo_collections, _tieredPromo_promos_, _product_purchase_information;
        if (!isLive) {
            return undefined;
        }
        // Markets have done discount matching on brand promotions, so check for that type
        var tieredPromo = brand === null || brand === void 0 ? void 0 : (_brand_active_promo_collections = brand.active_promo_collections) === null || _brand_active_promo_collections === void 0 ? void 0 : _brand_active_promo_collections.find(function(activePromoCollection) {
            return activePromoCollection.program === IBrandPromoCollection.Program.BRAND_PROMOTION;
        });
        if (!(tieredPromo === null || tieredPromo === void 0 ? void 0 : (_tieredPromo_promos_ = tieredPromo.promos[0]) === null || _tieredPromo_promos_ === void 0 ? void 0 : _tieredPromo_promos_.discount_bps)) {
            return undefined;
        }
        // Don't show ATS promo if the product is preorderable
        if ((tieredPromo === null || tieredPromo === void 0 ? void 0 : tieredPromo.product_ship_availability) === IBrandPromoCollection.ProductShipAvailability.ONLY_IMMEDIATE_PRODUCTS && ((_product_purchase_information = product.purchase_information) === null || _product_purchase_information === void 0 ? void 0 : _product_purchase_information.is_preorderable)) {
            return undefined;
        }
        // Show highest tier brand promo from the brand, if applicable.
        var promosFromBrand = tieredPromo === null || tieredPromo === void 0 ? void 0 : tieredPromo.promos;
        if (promosFromBrand) {
            return promosFromBrand[promosFromBrand.length - 1];
        }
        return undefined;
    }, [
        brand === null || brand === void 0 ? void 0 : brand.active_promo_collections,
        isLive,
        product
    ]);
};
export var ProductDetails = observer(ProductDetailsBase);
var Wrapper = styled(Column).withConfig({
    componentId: "sc-2088eb3e-0"
})([
    "width:100vw;padding:",
    ";",
    "{width:100%;padding:0;}"
], getSpacing(0, "2x"), responsiveMediaQueries.tabletAndAbove);
var StyledShowroomBadge = styled(ShowroomBadge).withConfig({
    componentId: "sc-2088eb3e-1"
})([
    "margin-bottom:12px;"
]);
